// src/pages/PostDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, MessageCircle, Lock, User } from 'lucide-react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Transaction, SystemProgram, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { useSolanaApp } from '../contexts/SolanaAppContext';
import { useLikes } from '../contexts/LikesContext';
import { useComments } from '../contexts/CommentsContext';
import { useToast } from '../contexts/ToastContext';
import { SupabaseService } from '../services/supabaseService';
import LikeButton from '../components/LikeButton';
import CommentButton from '../components/CommentButton';
import ShareButton from '../components/ShareButton';
import { supabase } from '../supabaseClient';
import { PLATFORM_CONFIG, calculateFees, formatFeeBreakdown } from '../config/platform';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { account, user, getMediaUrl } = useSolanaApp();
  const { initializeLikes } = useLikes();
  const { initializeComments, getComments, addComment } = useComments();
  const { toast } = useToast();

  // Solana wallet hooks for split payments
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const postComments = post ? getComments(post.id.toString()) : [];

  useEffect(() => {
    loadPost();
  }, [id, account]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📄 Loading post:', id);

      // Load post from Supabase
      const result = await SupabaseService.getPostById(id);

      if (!result.success || !result.data) {
        setError('Post not found');
        toast.error('Post not found');
        return;
      }

      const postData = result.data;
      console.log('✅ Post loaded:', postData);
      console.log('📊 Post media fields:', {
        media_url: postData.media_url,
        image_url: postData.image_url,
        content_hash: postData.content_hash
      });

      // Initialize likes and comments for this post
      await initializeLikes(id);
      await initializeComments(id);

      // Check access if it's a paid post
      if (postData.is_paid && account) {
        const normalizedAccount = account.toLowerCase();
        const accessResult = await SupabaseService.checkContentAccess(normalizedAccount, id);
        setHasAccess(accessResult.success ? accessResult.hasAccess : false);
      } else {
        setHasAccess(true); // Free posts are always accessible
      }

      setPost(postData);
    } catch (err) {
      console.error('❌ Error loading post:', err);
      setError('Failed to load post');
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

// src/pages/PostDetail.js - VERSIONE CORRETTA CON VALIDAZIONI
// ... (import e useState uguali)

const handlePurchase = async () => {
  // 🔍 DEBUG + VALIDAZIONE ROBUSTA
  console.log('🔍 handlePurchase DEBUG:', { 
    id, postIdType: typeof id, postIdValue: id,
    post, 
    postId: post?.id, 
    postPrice: post?.price 
  });

  if (!post || !account) {
    toast.error('Please connect your wallet');
    return;
  }

  // VALIDAZIONE post.id
  const postIdNum = parseInt(post.id);
  if (isNaN(postIdNum) || postIdNum <= 0) {
    console.error('❌ Invalid post ID:', post.id);
    toast.error(`Invalid post ID: ${post.id}`);
    return;
  }

  // VALIDAZIONE prezzo
  const priceSOL = parseFloat(post.price);
  if (isNaN(priceSOL) || priceSOL <= 0) {
    toast.error('Invalid post price');
    return;
  }

  // Calculate fees
  const fees = calculateFees(priceSOL);

  const confirmed = window.confirm(
    `Purchase access to this premium post for ${fees.total} SOL?\n\n` +
    formatFeeBreakdown(fees.total)
  );
  if (!confirmed) return;

  try {
    setPurchasing(true);
    toast.info('Processing payment with split (98% creator, 2% platform)...');

    if (!publicKey) {
      throw new Error('Wallet not connected');
    }

const platformPubkey = PLATFORM_CONFIG.WALLET_ADDRESS;

// Creator fallback: usa platform wallet se creator invalido
let creatorPubkey;
try {
  creatorPubkey = new PublicKey(post.creator_address);
} catch (e) {
  console.warn('👤 Creator address invalid, using platform wallet:', post.creator_address);
  creatorPubkey = platformPubkey; // Tutto al platform wallet
}


    // Create transaction con lamports ARROTONDATI
    const transaction = new Transaction();

    // 1. Creator (98%)
    const creatorLamports = Math.floor(fees.creatorAmount * LAMPORTS_PER_SOL);
    transaction.add(
      SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: creatorPubkey,
        lamports: creatorLamports,
      })
    );

    // 2. Platform (2%)
    const platformLamports = Math.floor(fees.platformFee * LAMPORTS_PER_SOL);
    transaction.add(
      SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: platformPubkey,
        lamports: platformLamports,
      })
    );

    // Send
    const signature = await sendTransaction(transaction, connection);
    toast.info('Confirming transaction...');
    await connection.confirmTransaction(signature, 'confirmed');

    toast.info('Recording purchase...');

    // SUPABASE con postIdNum
    const { error: purchaseError } = await supabase.from('postpurchases').insert({
      post_id: postIdNum,  // ← NUMERO invece di post.id
      buyer_address: publicKey.toString(),
      creator_address: post.creator_address,
      amount_paid: fees.total,
      platform_fee: fees.platformFee,
      creator_received: fees.creatorAmount,
      transaction_signature: signature,
      blockchain: 'solana'
    });

    if (purchaseError && purchaseError.code !== '23505') {
      throw purchaseError;
    }

    toast.success('✅ Post unlocked! Payment split completed.');
    await loadPost(); // Refresh access
  } catch (e) {
    console.error('❌ Purchase failed:', e);
    const msg = e?.message || String(e);
    if (msg.includes('rejected') || msg.includes('cancel') || msg.includes('user rejected')) {
      toast.info('Transaction cancelled');
    } else {
      toast.error(msg || 'Failed to purchase post');
    }
  } finally {
    setPurchasing(false);
  }
};


  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !post || !user) return;

    try {
      setSubmittingComment(true);

      const result = await addComment(
        post.id.toString(),
        newComment.trim(),
        user.wallet_address,
        user.username,
        user.avatar_url
      );

      if (result.success) {
        setNewComment('');
        // Toast is already shown by CommentsContext
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';

    const date = new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown date';
    }

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto text-purple-500" />
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Post Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The post you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="ml-4 text-lg font-semibold text-gray-900">Post</h1>
        </div>
      </div>

      {/* Post Content - Instagram style */}
      <div className="max-w-2xl mx-auto py-4">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {/* Post Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center overflow-hidden">
                {post.avatar_url && getMediaUrl(post.avatar_url) ? (
                  <img
                    src={getMediaUrl(post.avatar_url)}
                    alt={post.username}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <User className="h-6 w-6 text-white" style={post.avatar_url && getMediaUrl(post.avatar_url) ? { display: 'none' } : {}} />
              </div>
              <div className="ml-3">
                <h3
                  className="font-semibold text-gray-900 cursor-pointer hover:underline"
                  onClick={() => navigate(`/profile/${post.creator_address}`)}
                >
                  {post.username || 'Anonymous'}
                </h3>
                <p className="text-sm text-gray-500">{formatTimestamp(post.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Post Image/Content */}
          <div className="relative">
            {post.is_paid && !hasAccess ? (
              <div
                className="premium-content p-12 text-center bg-gray-50"
                data-premium="true"
                data-paid="true"
                onContextMenu={(e) => e.preventDefault()}
              >
                <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Content</h3>
                <p className="text-gray-600 mb-6">
                  This content requires payment to view
                </p>
                <div className="flex flex-col items-center space-y-4">
                  <span className="text-3xl font-bold text-purple-600">
                    {post.price} SOL
                  </span>
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="unlock-button bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 font-semibold"
                    data-unlock="true"
                    style={{
                      pointerEvents: 'auto',
                      zIndex: 9999,
                      position: 'relative',
                      touchAction: 'manipulation'
                    }}
                  >
                    {purchasing ? 'Processing...' : 'Purchase Access'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 flex items-center justify-center">
                {(() => {
                  // Get media URL - FIRST try media_url, fallback to image_url
                  const mediaUrl = post.media_url || post.image_url;

                  console.log('🖼️ Rendering media:', {
                    media_url: post.media_url,
                    image_url: post.image_url,
                    mediaUrl,
                    hasMedia: !!mediaUrl
                  });

                  if (!mediaUrl) {
                    return (
                      <div className="w-full h-48 flex items-center justify-center">
                        <p className="text-gray-500">No media available</p>
                      </div>
                    );
                  }

                  // Determine media source
                  // If base64 (data:image or data:video), use directly (temporary fix)
                  // Otherwise use getMediaUrl to handle Supabase URLs
                  let mediaSrc;
                  if (mediaUrl.startsWith('data:image') || mediaUrl.startsWith('data:video')) {
                    console.log('⚠️ Base64 detected - using directly (temporary fix)');
                    mediaSrc = mediaUrl;
                  } else {
                    mediaSrc = getMediaUrl(mediaUrl);
                  }

                  console.log('🔗 Media source:', mediaSrc ? mediaSrc.substring(0, 50) + '...' : 'null');

                  if (!mediaSrc) {
                    return (
                      <div className="w-full h-48 flex items-center justify-center">
                        <p className="text-gray-500">Media not found</p>
                      </div>
                    );
                  }

                  // Infer type from media URL: check if URL contains 'video'
                  const mediaType = (mediaUrl.toLowerCase().includes('video') || mediaSrc.startsWith('data:video')) ? 'video' : 'image';
                  console.log('🔍 Type inferred:', mediaType);

                  return mediaType === 'video' ? (
                    <video
                      src={mediaSrc}
                      controls
                      className="w-full max-h-[600px] object-contain"
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        console.error('❌ Video load error:', e);
                      }}
                    />
                  ) : (
                    <img
                      src={mediaSrc}
                      alt="Post content"
                      className="w-full max-h-[600px] object-contain"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        console.error('❌ Image load error:', e);
                      }}
                    />
                  );
                })()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex space-x-4">
                <LikeButton contentId={post.id.toString()} />
                <CommentButton
                  contentId={post.id.toString()}
                  contentAuthor={post.username || 'Anonymous'}
                />
                <ShareButton
                  contentId={post.id.toString()}
                  contentAuthor={post.username || 'Anonymous'}
                  contentDescription={post.caption || 'Check out this post!'}
                  showLabel={true}
                />
              </div>
            </div>

            {/* Caption */}
            {post.caption && (
              <div className="mt-2">
                <p className="text-gray-800">
                  <span className="font-semibold mr-2">{post.username}</span>
                  {post.caption}
                </p>
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <MessageCircle size={20} className="mr-2" />
              Comments ({postComments.length})
            </h3>

            {/* Comments List */}
            <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
              {postComments.length > 0 ? (
                postComments.map((comment) => {
                  // Get avatar URL from either the join (users.avatar_url) or direct field
                  const avatarUrl = comment.users?.avatar_url || comment.avatar_url;
                  const displayName = comment.users?.username || comment.username || 'Anonymous';

                  return (
                    <div key={comment.id} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0">
                        {avatarUrl && getMediaUrl(avatarUrl) ? (
                          <img
                            src={getMediaUrl(avatarUrl)}
                            alt={displayName}
                            className="w-full h-full object-cover rounded-full"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span
                          className="text-white text-xs font-semibold"
                          style={{ display: avatarUrl && getMediaUrl(avatarUrl) ? 'none' : 'flex' }}
                        >
                          {displayName.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm text-gray-900">
                            {displayName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm">{comment.content}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-8">No comments yet. Be the first to comment!</p>
              )}
            </div>

            {/* Add Comment Form */}
            {user ? (
              <form onSubmit={handleSubmitComment} className="flex items-center space-x-3 border-t border-gray-200 pt-4">
                <div className="w-8 h-8 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0">
                  {user.avatar_url && getMediaUrl(user.avatar_url) ? (
                    <img
                      src={getMediaUrl(user.avatar_url)}
                      alt={user.username}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span className="text-white text-xs font-semibold">
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={submittingComment}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submittingComment}
                  className="bg-purple-500 text-white px-6 py-2 rounded-full hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {submittingComment ? 'Posting...' : 'Post'}
                </button>
              </form>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p>Please log in to comment</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
